import styles from './index.module.scss';
import { FC, useMemo } from 'react';
import VaultDepositButton from '@components/vault-deposit-button';
import warnIcon from '@assets/images/vault/warn.svg';
import descIcon from '@assets/images/vault/desc.svg';
import sortIcon from '@assets/images/vault/sort-default.svg';
import Popover from '@ui/parrot/popover';
import { numericFormat } from '@utils/index';
import { OpenapiClientParrotVault } from 'src/openapi/parrot-vault';
import { useVaultCoin } from '@hooks/use-coins';
import { formatAddress } from '@mysten/sui/utils';
import copyIcon from '@assets/images/vault/copy.svg';
import copyHoverIcon from '@assets/images/vault/copy-hover.svg';
import { useCopyToClipboard, useToggle } from 'react-use';
import downIcon from '@assets/images/header/down.svg';
import VaultLoading from './loading';
import Nothing from '@components/nothing';
import { type MyVaultsStruct } from '@models/myvault.model';
// import { ClmmPoolUtil, TickMath } from '@cetusprotocol/cetus-sui-clmm-sdk';
// import { BN, Decimal } from 'parrot-sui-sdk';
// import { scaleDown } from '@utils/math';
import { type CoinStruct } from '@models/coins.vault.model';
import { globalSettingModel } from '@models/global-setting.model';
import { Link } from 'react-router-dom';
import ProtocolSelect from './protocol-select';
import { protocolList, type ProtocolListType } from '@constants/protocol-list';
import { notification } from 'antd';

interface OwnProps {
  vaults: OpenapiClientParrotVault.GetVaultVaultsResponse | undefined;
  sortValue: OpenapiClientParrotVault.GetVaultVaultsQuery['sort'] | undefined;
  sortType: OpenapiClientParrotVault.GetVaultVaultsQuery['direction'] | undefined;
  handleSort(value: OpenapiClientParrotVault.GetVaultVaultsQuery['sort'] | undefined): void;
  myVaults?: MyVaultsStruct[];
  protocolValue: ProtocolListType;
  onChangeProtocolValue(value: ProtocolListType): void;
}

const VaultItem: FC<OwnProps> = ({
  sortType,
  sortValue,
  handleSort,
  vaults,
  myVaults,
  protocolValue,
  onChangeProtocolValue,
}) => {
  const [protocolVisible, toggleProtocolVisible] = useToggle(false);
  const { tvlSort, tvlClassName } = useMemo(() => {
    if (sortValue === 'liquidity_usd') {
      return {
        tvlSort: descIcon,
        tvlClassName: sortType === 'asc' ? styles.asc_img : '',
      };
    }
    return {
      tvlSort: sortIcon,
      tvlClassName: '',
    };
  }, [sortType, sortValue]);

  const { aprSort, aprClassName } = useMemo(() => {
    if (sortValue === 'apy') {
      return {
        aprSort: descIcon,
        aprClassName: sortType === 'asc' ? styles.asc_img : '',
      };
    }
    return {
      aprSort: sortIcon,
      aprClassName: '',
    };
  }, [sortType, sortValue]);

  if (vaults && vaults.result.length === 0) {
    return <Nothing text="No vaults found" />;
  }

  return (
    <>
      <div className={styles.table_tr}>
        <div>Vault</div>
        <div className={styles.flex_center}>
          <div className={`${styles.sort}`} onClick={() => handleSort('apy')}>
            <span>APY</span>
            <Popover
              content={
                <div className={styles.apr_tip} onClick={(e) => e.stopPropagation()}>
                  APY is estimated according to the trading fees and rewards earned over the past 24
                  hours with daily compounding to be considered.
                </div>
              }
              trigger={'hover'}
            >
              <img src={warnIcon} onClick={(e) => e.stopPropagation()} className={styles.default} />
            </Popover>
            <img src={aprSort} className={`${styles.sort_icon} ${aprClassName}`} />
          </div>
        </div>
        <div className={`${styles.flex_center}`}>
          <div className={`${styles.sort}`} onClick={() => handleSort('liquidity_usd')}>
            <span>TVL</span>
            <img src={tvlSort} className={`${styles.sort_icon} ${tvlClassName}`} />
          </div>
        </div>

        <Popover
          content={
            <ProtocolSelect
              protocolValue={protocolValue}
              onChangeProtocolValue={onChangeProtocolValue}
            />
          }
          trigger={'click'}
          placement={'bottom'}
          open={protocolVisible}
          onOpenChange={toggleProtocolVisible}
        >
          <div className={styles.flex_center}>
            <div className={styles.protocol_select}>
              Protocol
              <img
                src={downIcon}
                data-icon
                className={`${styles.protocol} ${protocolVisible ? styles.protocol_active : ''}`}
              />
            </div>
          </div>
        </Popover>
        <div className={styles.center}>Deposited</div>
        <div className={styles.right}>Actions</div>
      </div>
      {!vaults ? (
        <VaultLoading />
      ) : (
        <>
          {vaults.result.map((vault) => {
            return <VaultList key={vault.vault_id} vault={vault} myVaults={myVaults} />;
          })}
        </>
      )}
    </>
  );
};

const VaultList: FC<{
  vault: OpenapiClientParrotVault.GetVaultVaultsResponse['result'][number];
  myVaults?: MyVaultsStruct[];
}> = ({ vault, myVaults }) => {
  const { coin: coinA } = useVaultCoin(vault.coin_type_a);
  const { coin: coinB } = useVaultCoin(vault.coin_type_b);

  // const myVault = myVaults?.find((item) => item.vault_id === vault.vault_id);
  // let deposited_usd = new Decimal(0);
  // if (myVault) {
  //   const coinAmounts = ClmmPoolUtil.getCoinAmountFromLiquidity(
  //     new BN(myVault.liquidity),
  //     new BN(myVault.current_sqrt_price),
  //     TickMath.tickIndexToSqrtPriceX64(myVault.tick_lower_index),
  //     TickMath.tickIndexToSqrtPriceX64(myVault.tick_upper_index),
  //     false,
  //   );

  //   deposited_usd = deposited_usd
  //     .add(scaleDown(coinAmounts.coinA.toString(), coinA?.decimals || 9).mul(coinA?.price_usd || 0))
  //     .add(scaleDown(coinAmounts.coinB.toString(), coinB?.decimals || 9).mul(coinB?.price_usd || 0));
  // }

  const protocol = protocolList.find((list) => list.label === vault.protocol);
  return (
    <>
      <div className={styles.table_td}>
        <Popover
          content={<VaultListInfo coinA={coinA} coinB={coinB} vaultId={vault.vault_id} />}
          trigger={'hover'}
          placement={'top'}
        >
          <div className={styles.flex_left}>
            <img src={coinA?.icon || ''} className={styles.symbol_icon} />
            <img src={coinB?.icon || ''} className={styles.symbol_icon} />
            <span className={styles.symbol_name}>
              {coinA?.symbol} - {coinB?.symbol}
            </span>
          </div>
        </Popover>
        <div className={styles.center}>{numericFormat(vault.apy)}%</div>
        <div className={styles.center}>${numericFormat(vault.liquidity_usd)}</div>
        <div className={styles.flex_center}>
          <img src={protocol?.icon} className={styles.protocol_img} />
          <span>{vault.protocol}</span>
        </div>
        <div className={styles.center}>${numericFormat(vault.balance)}</div>
        <div className={styles.flex_right}>
          <VaultDepositButton vault={vault} />
        </div>
      </div>
      <VaultListMobile vault={vault} depositedUsd={vault.balance} coinA={coinA} coinB={coinB} />
    </>
  );
};

const VaultListInfo: FC<{
  vaultId: string;
  coinA: OpenapiClientParrotVault.GetVaultCoinsResponse[number] | undefined;
  coinB: OpenapiClientParrotVault.GetVaultCoinsResponse[number] | undefined;
}> = ({ vaultId, coinA, coinB }) => {
  const [, copy] = useCopyToClipboard();
  return (
    <div className={styles.info}>
      <div className={styles.info_vault}>
        <span>Vault</span>
        <Link
          to={globalSettingModel.explorerUrlForObject(vaultId)}
          target={'_blank'}
          className={styles.link}
        >
          {formatAddress(vaultId)}
        </Link>
        <div
          className={styles.copy}
          onClick={() => {
            copy(vaultId);
            notification.open({
              message: 'Vault address is copied',
            });
          }}
        >
          <img src={copyIcon} data-icon />
          <img src={copyHoverIcon} data-icon className={styles.hover} />
        </div>
      </div>
      <div className={styles.info_symbol}>
        <img src={coinA?.icon || ''} className={styles.info_symbol_icon} />
        <span className={styles.info_symbol_name}>{coinA?.symbol}</span>

        <Link
          to={globalSettingModel.explorerUrlForCoin(coinA?.coin_type || '')}
          target={'_blank'}
          className={styles.link}
        >
          {formatAddress(coinA?.coin_type || '')}
        </Link>
        <div
          className={styles.copy}
          onClick={() => {
            copy(coinA?.coin_type || '');
            notification.open({
              message: 'Coin address is copied',
            });
          }}
        >
          <img src={copyIcon} data-icon />
          <img src={copyHoverIcon} data-icon className={styles.hover} />
        </div>
      </div>
      <div className={styles.info_symbol}>
        <img src={coinB?.icon || ''} className={styles.info_symbol_icon} />
        <span className={styles.info_symbol_name}>{coinB?.symbol}</span>
        <Link
          to={globalSettingModel.explorerUrlForCoin(coinB?.coin_type || '')}
          target={'_blank'}
          className={styles.link}
        >
          {formatAddress(coinB?.coin_type || '')}
        </Link>

        <div
          className={styles.copy}
          onClick={() => {
            copy(coinB?.coin_type || '');
            notification.open({
              message: 'Coin address is copied',
            });
          }}
        >
          <img src={copyIcon} data-icon />
          <img src={copyHoverIcon} data-icon className={styles.hover} />
        </div>
      </div>
    </div>
  );
};

const VaultListMobile: FC<{
  vault: OpenapiClientParrotVault.GetVaultVaultsResponse['result'][number];
  depositedUsd: string;
  coinA: CoinStruct | undefined;
  coinB: CoinStruct | undefined;
}> = ({ vault, depositedUsd, coinA, coinB }) => {
  const protocol = protocolList.find((list) => list.label === vault.protocol);

  return (
    <div className={styles.mobile}>
      <div className={styles.flex_left}>
        <img src={coinA?.icon || ''} className={styles.symbol_icon} />
        <img src={coinB?.icon || ''} className={styles.symbol_icon} />
        <span className={styles.symbol_name}>
          {coinA?.symbol} - {coinB?.symbol}
        </span>
      </div>
      <div className={styles.line}>
        <div className={styles.line_title}>APY</div>
        <div className={styles.line_value}>{numericFormat(vault.apr)}%</div>
      </div>
      <div className={styles.line}>
        <div className={styles.line_title}>TVL</div>
        <div className={styles.center}>${numericFormat(vault.liquidity_usd)}</div>
      </div>
      <div className={styles.line}>
        <div className={styles.line_title}>Protocol</div>
        <div className={styles.flex_right}>
          <img src={protocol?.icon} className={styles.protocol_img} />
          <span>{vault.protocol}</span>
        </div>
      </div>
      <div className={styles.line_last}>
        <div className={styles.line_title}>Deposited</div>
        <div className={styles.center}>${numericFormat(depositedUsd)}</div>
      </div>
      <div className={styles.line_btn}>
        <VaultDepositButton vault={vault} />
      </div>
    </div>
  );
};

export default VaultItem;
